import { getCurrentProject } from '@/lib/current-project';
import type { ThemeColor } from '@shared/types/theme';
import { toCalendarYmd, ymdToUtcDate } from '@/lib/ymd';
import {
  CURRENCY_PRESETS,
  NUMBER_PRESETS,
  PERCENT_PRESETS,
  type CurrencyPreset,
  type NumberPreset,
  type PercentPreset,
} from './format-presets';

/**
 * Every number, amount, percent and date the app shows goes through here, in en-US numbers and the
 * current project's timezone (UTC outside a project). Missing values (null, undefined, NaN, a bad
 * date) show EMPTY; zero shows as 0. Rules: formatting.mdc, formatting.project.mdc.
 */

/** The settings numbers, money and dates are formatted with. */
export interface FormatSettings {
  numberFormat?: string;
  currency?: string;
  timezone?: string;
  currencyDisplay?: 'symbol' | 'code' | 'name' | 'narrowSymbol';
  themeColor?: ThemeColor;
}

export { EMPTY };

let settingsOverride: FormatSettings | null = null;

/** Format with these settings instead of the current project's (the /dev/ui gallery, tests); null to stop. */
export function setFormatSettings(settings: FormatSettings | null): void {
  settingsOverride = settings;
}

/** The project's number format, for reading typed and pasted numbers (parseMoneyInput). */
export const projectNumberLocale = (): string => getFormatSettings().numberFormat || 'en-US';

/** The project's currency code, with the same fallback as formatCurrency. */
export const projectCurrency = (): string => getFormatSettings().currency || 'USD';

/** The project's settings, or en-US, USD and UTC when none are loaded. */
export const getFormatSettings = (): FormatSettings => {
  return (
    getSavedFormatSettings() ?? {
      numberFormat: 'en-US',
      currency: 'USD',
      timezone: 'UTC',
      currencyDisplay: 'symbol',
    }
  );
};

/**
 * The settings in force: an override (the /dev/ui gallery, tests), else the current project's
 * timezone; null outside a project. getFormatSettings() then falls back to en-US, USD and UTC.
 */
export const getSavedFormatSettings = (): FormatSettings | null => {
  if (settingsOverride) return settingsOverride;
  const project = getCurrentProject();
  return project ? { numberFormat: 'en-US', currency: 'USD', timezone: project.timezone, currencyDisplay: 'symbol' } : null;
};

/**
 * The project's timezone (Project settings), or UTC when it has none or Intl doesn't know it
 * (the Worker does the same). Never the browser's zone.
 */
export function projectTimezone(): string {
  const zone = getSavedFormatSettings()?.timezone?.trim();
  if (!zone) return 'UTC';
  try {
    cachedDateFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return 'UTC';
  }
}

/**
 * The browser's own timezone, or UTC when Intl can't say. Only for what's about the person rather than
 * a project: the time calendar (T-108) places people's minutes in their own day. Everything else shows
 * the project's (projectTimezone).
 */
export function browserTimezone(): string {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!zone) return 'UTC';
    cachedDateFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return 'UTC';
  }
}

/** The project's locale, which dates and numbers are shown in (DateField reads typed dates in it too). */
export const projectLocale = (): string => getFormatSettings().numberFormat || 'en-US';

// ---- Cached Intl formatters: building one costs far more than using it. ----

const intlCache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat>();

function cached<T extends Intl.NumberFormat | Intl.DateTimeFormat>(key: string, make: () => T): T {
  const hit = intlCache.get(key) as T | undefined;
  if (hit) return hit;
  const made = make();
  // Option sets come from code, so this stays small; the cap is a backstop.
  if (intlCache.size >= 500) intlCache.clear();
  intlCache.set(key, made);
  return made;
}

function cachedNumberFormat(locale: string, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  return cached(`n|${locale}|${JSON.stringify(options)}`, () => new Intl.NumberFormat(locale, options));
}

function cachedDateFormat(locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return cached(`d|${locale}|${JSON.stringify(options)}`, () => new Intl.DateTimeFormat(locale, options));
}

// ---- Numbers, money and percentages ----

type Maybe<T> = T | null | undefined;

/** A number from a number or numeric string (pg numeric arrives as text); null when there is none. */
function finiteOrNull(value: Maybe<number | string>): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

// The presets live in format-presets.ts, which has no imports.
export {
  CURRENCY_PRESETS,
  NUMBER_PRESETS,
  PERCENT_PRESETS,
  type CurrencyPreset,
  type NumberPreset,
  type PercentPreset,
} from './format-presets';
import { EMPTY } from './empty';

/** A number in the project's format; EMPTY when there is none. */
export const formatNumber = (
  value: Maybe<number | string>,
  options: Intl.NumberFormatOptions | NumberPreset = {}
): string => {
  const n = finiteOrNull(value);
  if (n == null) return EMPTY;
  const opts = typeof options === 'string' ? NUMBER_PRESETS[options] : options;
  return cachedNumberFormat(projectLocale(), opts).format(n);
};

/** An amount in the project's currency and format; EMPTY when there is none. */
export const formatCurrency = (
  value: Maybe<number | string>,
  options: Intl.NumberFormatOptions | CurrencyPreset = {}
): string => {
  const n = finiteOrNull(value);
  if (n == null) return EMPTY;
  const settings = getFormatSettings();
  let opts: Intl.NumberFormatOptions;
  if (options === 'price') opts = Number.isInteger(n) ? CURRENCY_PRESETS.whole : {};
  else opts = typeof options === 'string' ? CURRENCY_PRESETS[options] : options;
  return cachedNumberFormat(settings.numberFormat || 'en-US', {
    style: 'currency',
    currency: settings.currency || 'USD',
    currencyDisplay: settings.currencyDisplay || 'symbol',
    ...opts,
  }).format(n);
};

/** A fraction as a percent (0.123 → "12.3%" with 'ratio'); EMPTY when there is none. */
export function formatPercent(
  fraction: Maybe<number | string>,
  options: Intl.NumberFormatOptions | PercentPreset = 'ratio'
): string {
  const n = finiteOrNull(fraction);
  if (n == null) return EMPTY;
  const opts = typeof options === 'string' ? PERCENT_PRESETS[options] : options;
  return cachedNumberFormat(projectLocale(), { style: 'percent', ...opts }).format(n);
}

/** A change between two percentages, in points: 1.2 → "+1.2 pp". */
export function formatPointChange(points: Maybe<number | string>, decimals = 1): string {
  const n = finiteOrNull(points);
  if (n == null) return EMPTY;
  const text = cachedNumberFormat(projectLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: 'exceptZero',
  }).format(n);
  return `${text} pp`;
}

// ---- Dates and times ----

type DateInput = Maybe<Date | string | number>;

const DATE_FIELDS = [
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dayPeriod',
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'timeZoneName',
  'dateStyle',
  'timeStyle',
] as const;

/** Options that already say what to show (Intl refuses dateStyle beside fields like `month`). */
const choosesFields = (options: Intl.DateTimeFormatOptions, fields: readonly string[] = DATE_FIELDS) =>
  fields.some((field) => options[field as keyof Intl.DateTimeFormatOptions] !== undefined);

/** The fields that can't sit beside dateStyle or timeStyle. */
const COMPONENT_FIELDS = DATE_FIELDS.filter((f) => f !== 'dateStyle' && f !== 'timeStyle');

interface DatePresetDef {
  options: Intl.DateTimeFormatOptions;
  /** Add the year only when it isn't this year (in the project's timezone). */
  yearIfNotThis?: boolean;
}

/** Named formats for formatDate and formatDay ('medium' is the default). */
export const DATE_PRESETS = {
  /** 5 Oct 2026 (en-GB), Oct 5, 2026 (en-US) */
  medium: { options: { dateStyle: 'medium' } },
  /** 5 October 2026 */
  long: { options: { dateStyle: 'long' } },
  /** 05/10/2026 */
  numeric: { options: { day: '2-digit', month: '2-digit', year: 'numeric' } },
  /** Oct 2026 */
  monthYear: { options: { month: 'short', year: 'numeric' } },
  /** 5 Oct (and the year when it isn't this one) */
  short: { options: { day: 'numeric', month: 'short' }, yearIfNotThis: true },
  /** Mon 5 Oct (and the year when it isn't this one) */
  weekday: { options: { weekday: 'short', day: 'numeric', month: 'short' }, yearIfNotThis: true },
} satisfies Record<string, DatePresetDef>;
export type DatePreset = keyof typeof DATE_PRESETS;

/** Named formats for formatDateTime ('medium' is the default). */
export const DATE_TIME_PRESETS = {
  /** 5 Oct 2026, 14:30 */
  medium: { options: { dateStyle: 'medium', timeStyle: 'short' } },
  /** 5 Oct, 14:30 (and the year when it isn't this one): the short date and the locale's short time. */
  short: { options: { day: 'numeric', month: 'short' }, yearIfNotThis: true },
} satisfies Record<string, DatePresetDef>;
export type DateTimePreset = keyof typeof DATE_TIME_PRESETS;

function toValidDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** Resolve a preset or options to Intl options, with defaults used only when nothing is chosen. */
function dateOptions(
  options: Intl.DateTimeFormatOptions | string,
  presets: Record<string, DatePresetDef>,
  defaults: Intl.DateTimeFormatOptions,
  sameYear: () => boolean,
  /** Keep the default dateStyle/timeStyle when the options set only the other (formatDateTime). */
  mergeStyles = false
): Intl.DateTimeFormatOptions {
  if (typeof options === 'string') {
    const preset = presets[options] ?? presets.medium!;
    return preset.yearIfNotThis && !sameYear() ? { ...preset.options, year: 'numeric' } : preset.options;
  }
  return choosesFields(options, mergeStyles ? COMPONENT_FIELDS : DATE_FIELDS) ? options : { ...defaults, ...options };
}

/** The year at `date` in `timeZone`, through a cached formatter (en-US writes it as plain digits). */
function zonedYear(date: Date, timeZone: string): string {
  return cachedDateFormat('en-US', { timeZone, year: 'numeric' }).format(date);
}

/**
 * An instant's date in the project's timezone: "5 Oct 2026" by default. A plain
 * "YYYY-MM-DD" is read as a calendar day (formatDay), so it never slips a day.
 */
export const formatDate = (
  date: DateInput,
  options: Intl.DateTimeFormatOptions | DatePreset = {}
): string => {
  // Date-shaped text is a calendar day; an impossible one ("2026-02-30") shows EMPTY, not 2 March.
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) return formatDay(date, options);
  const d = toValidDate(date);
  if (!d) return EMPTY;
  const timeZone = (typeof options === 'object' && options.timeZone) || projectTimezone();
  const opts = dateOptions(options, DATE_PRESETS, DATE_PRESETS.medium.options, () =>
    zonedYear(d, timeZone) === zonedYear(new Date(), timeZone)
  );
  return cachedDateFormat(projectLocale(), { ...opts, timeZone }).format(d);
};

/** An instant's date and time in the project's timezone: "5 Oct 2026, 14:30" by default. */
export const formatDateTime = (
  date: DateInput,
  options: Intl.DateTimeFormatOptions | DateTimePreset = {}
): string => {
  const d = toValidDate(date);
  if (!d) return EMPTY;
  // Intl can't mix timeStyle with day and month, and hour: 'numeric' writes 0:30 in en-GB.
  if (options === 'short') return `${formatDate(d, 'short')}, ${formatTime(d, { timeStyle: 'short' })}`;
  const timeZone = (typeof options === 'object' && options.timeZone) || projectTimezone();
  const opts = dateOptions(
    options,
    DATE_TIME_PRESETS,
    DATE_TIME_PRESETS.medium.options,
    () => zonedYear(d, timeZone) === zonedYear(new Date(), timeZone),
    true
  );
  return cachedDateFormat(projectLocale(), { ...opts, timeZone }).format(d);
};

/** An instant's time in the project's timezone: "14:30:05" by default. */
export const formatTime = (date: DateInput, options: Intl.DateTimeFormatOptions = {}): string => {
  const d = toValidDate(date);
  if (!d) return EMPTY;
  const opts = choosesFields(options, COMPONENT_FIELDS) ? options : { timeStyle: 'medium' as const, ...options };
  return cachedDateFormat(projectLocale(), { timeZone: projectTimezone(), ...opts }).format(d);
};

/**
 * A calendar day (no time, no zone): "2026-10-05", or that day as UTC midnight the way pg DATE
 * columns arrive. Shown as written, whatever the project's or browser's timezone. EMPTY otherwise.
 */
export function formatDay(
  ymd: Maybe<string>,
  options: Intl.DateTimeFormatOptions | DatePreset = 'medium'
): string {
  const day = toCalendarYmd(ymd);
  if (!day) return EMPTY;
  const opts = dateOptions(options, DATE_PRESETS, DATE_PRESETS.medium.options, () =>
    day.slice(0, 4) === zonedYear(new Date(), projectTimezone())
  );
  return cachedDateFormat(projectLocale(), { ...opts, timeZone: 'UTC' }).format(ymdToUtcDate(day));
}

/**
 * Two calendar days as one range, the way the locale writes it: "1–31 Oct 2026" (en-GB).
 * One missing side shows the other day; both missing show EMPTY.
 */
export function formatDayRange(
  from: Maybe<string>,
  to: Maybe<string>,
  options: Intl.DateTimeFormatOptions | DatePreset = 'medium'
): string {
  const a = toCalendarYmd(from);
  const b = toCalendarYmd(to);
  if (!a || !b) return formatDay(a ?? b, options);
  const thisYear = zonedYear(new Date(), projectTimezone());
  const opts = dateOptions(
    options,
    DATE_PRESETS,
    DATE_PRESETS.medium.options,
    () => a.slice(0, 4) === thisYear && b.slice(0, 4) === thisYear
  );
  const format = cachedDateFormat(projectLocale(), { ...opts, timeZone: 'UTC' });
  const start = ymdToUtcDate(a);
  const end = ymdToUtcDate(b);
  return typeof format.formatRange === 'function'
    ? format.formatRange(start, end)
    : `${format.format(start)} – ${format.format(end)}`;
}

/**
 * How long ago (or until) an instant: "just now", "5m ago", "3h ago", "2d ago", "in 4h"; from a
 * week away, the date ("5 Oct", with the year when it isn't this one).
 */
export function formatRelative(at: DateInput, now: Date | number = Date.now()): string {
  const d = toValidDate(at);
  if (!d) return EMPTY;
  const diff = (now instanceof Date ? now.getTime() : now) - d.getTime();
  const minutes = Math.round(Math.abs(diff) / 60_000);
  const say = (amount: string) => (diff >= 0 ? `${amount} ago` : `in ${amount}`);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return say(`${minutes}m`);
  const hours = Math.round(minutes / 60);
  if (hours < 24) return say(`${hours}h`);
  const days = Math.round(hours / 24);
  if (days < 7) return say(`${days}d`);
  return formatDate(d, 'short');
}

// ---- Other units ----

/**
 * Format a byte size for media/file metadata display.
 * Accepts numbers or numeric strings (Postgres bigint often serializes as string).
 * Returns EMPTY for null/undefined/NaN/negative values.
 */
export function formatBytes(n: number | string | null | undefined): string {
  const value = finiteOrNull(n);
  if (value == null || value < 0) return EMPTY;
  if (value < 1024) return `${Math.round(value)} B`;
  const units = ['KB', 'MB', 'GB', 'TB'] as const;
  let scaled = value / 1024;
  let unitIndex = 0;
  while (scaled >= 1024 && unitIndex < units.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }
  const rounded =
    scaled >= 100
      ? Math.round(scaled)
      : scaled >= 10
        ? Math.round(scaled * 10) / 10
        : Math.round(scaled * 100) / 100;
  return `${rounded} ${units[unitIndex]}`;
}
